import { MentionEvent } from 'interfaces/mentionEvent';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { CREATE_CONVERSATION } from 'schema-v2/Chat/mutations';
import { CHANNEL_TYPE, UNIFIED_GRAPH } from 'utils/constants';
import { rawId } from 'utils/rawId';
import { goToConversation } from 'utils/routerHelpers';

import React, { UIEvent, useCallback, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';

import { LoadingSpinner, Modal, SearchField, Text } from '@thinkific/chalk';
import { MenuItemWithSymbols } from '@thinkific/chalk/recipes/MenuItemWithSymbols';
import { toaster } from '@thinkific/toga-react';

import { useCommunityContext } from 'components/CommunityContext';
import withUsersSearch from 'components/Mentions';
import { mapUserObject } from 'components/Mentions/MentionsTextArea/helpers/mention-helpers';
import UserAvatar from 'components/UserAvatar';

import { useChatContext } from '../chat/providers/ChatProvider/ChatProvider';
import * as css from './NewConversationModal.css';

const NewConversationModal = ({
  handleSearch,
}: {
  handleSearch: (search: string) => Promise<MentionEvent[]>;
}) => {
  const { t } = useTranslation();
  const { isNewChannelModalOpen, toggleNewConversationModal, session, findExistingConversation } =
    useChatContext();
  const { currentUser, community, mentionableUsers } = useCommunityContext();

  const router = useRouter();

  const [createConversation] = useMutation(CREATE_CONVERSATION, {
    context: { clientName: UNIFIED_GRAPH },
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchableUsers, setSearchableUsers] = useState<MentionEvent[]>([]);
  const [loadingConversationUserId, setLoadingConversationUserId] = useState('');

  const navigateToConversation = useCallback(
    (conversationId: string) => {
      goToConversation(router, community.id, conversationId);
      toggleNewConversationModal();
    },
    [community.id, router, toggleNewConversationModal]
  );

  const handleModalClose = useCallback(() => {
    setSearchTerm('');
    setLoadingConversationUserId('');
    toggleNewConversationModal();
  }, [toggleNewConversationModal, setLoadingConversationUserId, setLoadingConversationUserId]);

  useEffect(() => {
    if (searchTerm.trim()) {
      handleSearch(searchTerm).then((users) => {
        setSearchableUsers(users.filter((user) => user.id !== currentUser.id));
      });
    } else {
      setSearchableUsers(
        mapUserObject(
          mentionableUsers?.edges?.filter((user) => user.node.user.id !== currentUser.id)
        )
      );
    }
  }, [searchTerm, handleSearch]);

  const handleAddMember = useCallback(
    async (user) => {
      if (!session) {
        toaster.alert(
          t(
            'communities_components-NewConversationModal-no_session',
            'Something went wrong. Please refresh the page to try again.'
          )
        );
        return;
      }
      setLoadingConversationUserId(user.id);
      const existingConversation = await findExistingConversation([currentUser.gid, user.gid]);

      if (existingConversation?.id) {
        setLoadingConversationUserId('');
        navigateToConversation(existingConversation.id);
        return;
      }

      const {
        data: {
          createConversation: { userErrors, conversation },
        },
      } = await createConversation({
        variables: {
          input: {
            conversationType: CHANNEL_TYPE,
            conversationUsers: [{ id: currentUser.gid }, { id: user.gid }],
            metadata: { communityId: rawId(community.id)?.toString() },
          },
        },
      });
      setLoadingConversationUserId('');
      if (userErrors?.length > 0) {
        for (const error of userErrors) {
          toaster.alert(error.message);
        }
      } else {
        navigateToConversation(conversation.id);
      }
    },
    [currentUser.gid, createConversation, findExistingConversation, navigateToConversation, session]
  );

  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <Modal
      className={css.newConversationModal}
      contentSurface="primary"
      open={isNewChannelModalOpen}
      title={t(
        'communities_components-NewConversationModal-start_a_conversation',
        'Start a conversation'
      )}
      onClose={handleModalClose}
    >
      <div className={css.container}>
        <SearchField
          className={css.searchField}
          dataQa="create-conversation-modal-search-field"
          placeholder={t(
            'add-members-to-space-search-by-name-input',
            'Search by first or last name'
          )}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e)}
          onClear={() => setSearchTerm('')}
        />

        <div className={css.resultsContainer} onScroll={handleScroll}>
          {searchableUsers.map((user) => (
            <MenuItemWithSymbols
              endSymbol={loadingConversationUserId === user.id && <LoadingSpinner />}
              key={user.id}
              label={user.display}
              leadSymbol={
                <UserAvatar
                  avatarUrl={user.avatarUrl}
                  size="xs"
                  srcSet={`${user.avatarUrl}?width=40 1x, ${user.avatarUrl}?width=40&dpr=2 2x, ${user.avatarUrl}?width=40&dpr=3 3x`}
                  userId={user.id}
                  username={user.display}
                />
              }
              onClick={() => handleAddMember(user)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddMember(user);
                }
              }}
            />
          ))}
          {searchableUsers.length === 0 && (
            <div className={css.emptyState}>
              <Text>
                {t(
                  'components-NewConversationModal-no_matching_users_found',
                  'No matching users found'
                )}
              </Text>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default withUsersSearch(NewConversationModal, true, 10);
